import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import SickIcon from "../../assets/icons/leave_icons/sick_leave.svg";
import CasualIcon from "../../assets/icons/leave_icons/casual.svg";
import PrivilageIcon from "../../assets/icons/leave_icons/privilage_leave.svg";
import MaternityIcon from "../../assets/icons/leave_icons/maternity.svg";
import MenstrualIcon from "../../assets/icons/leave_icons/menstrual.svg";
import DocsIcon from "../../assets/icons/leave_icons/docs.svg";
import ApproveStatusIcon from "../../assets/icons/approve_status.svg";
import RejectStatusIcon from "../../assets/icons/reject_status.svg";
import PendingStatusIcon from "../../assets/icons/pending_status.svg";
import CancelStatusIcon from "../../assets/icons/cancel_status.svg";
import CommentIcon from "../../assets/icons/comment.svg";
import MessageModal from "../../commonComponents/messageModal";
import TwoArrowIcon from "../../assets/icons/arrowsided.svg";
import NothingIcon from "../../assets/images/task-not-found-1 6.png";
import moment from "moment";
import "./LeaveHistory.css";
import { PendingCardSkeletons } from "../../utils/skeleton";

const LeaveHistory = ({ leaveDetailResponse, onCancelLeave, onEditLeave }) => {
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    message: "",
  });
  const tooltipRef = useRef(null);
  const empName = useSelector(
    (state) => state?.auth?.loginResponse?.display_name,
  );
  const leaveDetailResponseStatus = useSelector(
    (state) => state.attendance.leaveDetailResponseStatus,
  );

  const totalItems = leaveDetailResponse?.body?.length || 0;
  const activeShowAbove =
    activeTooltip !== null
      ? activeTooltip > 2 && activeTooltip >= totalItems - 4
      : false;
  const itemsBelow =
    activeTooltip !== null ? totalItems - 1 - activeTooltip : 0;
  const paddingBottomStr =
    activeTooltip !== null && !activeShowAbove && itemsBelow < 4
      ? "260px"
      : "0px";

  // console.log("empName-@@@->", empName)

  // console.log("leaveDetailResponse-@@@->", leaveDetailResponse)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        setActiveTooltip(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (activeTooltip !== null && tooltipRef.current) {
      setTimeout(() => {
        tooltipRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 100);
    }
  }, [activeTooltip]);

  /* ===== Helper: Generate One Month Calendar ===== */
  const generateMonth = (
    year,
    month,
    start,
    end,
    halfDayDate = null,
    halfDaySide = null,
  ) => {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    const prevMonthDays = new Date(year, month, 0).getDate();

    const cells = [];

    // Previous month
    for (let i = firstDay - 1; i >= 0; i--) {
      cells.push(
        <div key={`prev-${i}`} className="micro-day micro-disabled">
          {prevMonthDays - i}
        </div>,
      );
    }

    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
      const current = new Date(year, month, d);
      const isActive = current >= start && current <= end;
      // Check if this specific day is the half-day date
      const isHalfDay =
        halfDayDate &&
        current.getFullYear() === halfDayDate.getFullYear() &&
        current.getMonth() === halfDayDate.getMonth() &&
        current.getDate() === halfDayDate.getDate();

      let cellClass = "micro-day micro-dual-box-cell";
      let content = d;

      if (isHalfDay) {
        const isFirstHalfLeave = halfDaySide === "first";
        // first_half: "half_day" → LEFT GREY, RIGHT BLACK
        // second_half: "half_day" → LEFT BLACK, RIGHT GREY
        content = (
          <>
            <div
              className={`dual-box ${isFirstHalfLeave ? "dual-box-light" : "dual-box-dark"}`}
            ></div>
            <div
              className={`dual-box ${isFirstHalfLeave ? "dual-box-dark" : "dual-box-light"}`}
            ></div>
            <span className="day-num">{d}</span>
          </>
        );
      } else if (isActive) {
        // Full day: both boxes are black
        content = (
          <>
            <div className="dual-box dual-box-dark"></div>
            <div className="dual-box dual-box-dark"></div>
            <span className="day-num">{d}</span>
          </>
        );
      }

      cells.push(
        <div key={`current-${d}`} className={cellClass}>
          {content}
        </div>,
      );
    }

    // Fill remaining grid
    const totalCells = cells.length;
    const remaining = totalCells <= 35 ? 35 - totalCells : 42 - totalCells;

    for (let i = 1; i <= remaining; i++) {
      cells.push(
        <div key={`next-${i}`} className="micro-day micro-disabled">
          {i}
        </div>,
      );
    }

    return (
      <div className="micro-month" key={`${month}-${year}`}>
        <div className="micro-month-title">
          {new Date(year, month).toLocaleString("default", {
            month: "long",
          })}{" "}
          {year}
        </div>
        <div className="micro-grid">{cells}</div>
      </div>
    );
  };

  /* ===== Tooltip Renderer ===== */
  // Parse "YYYY-MM-DD" as LOCAL midnight (avoids UTC offset shifting the date)
  const parseLocalDate = (dateStr) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  const renderMiniCalendar = (
    startDate,
    endDate,
    showAbove = false,
    halfDay = null,
  ) => {
    const start = parseLocalDate(startDate);
    const end = parseLocalDate(endDate);

    const startMonth = start.getMonth();
    const startYear = start.getFullYear();
    const endMonth = end.getMonth();
    const endYear = end.getFullYear();

    // Determine half-day date and which half is on leave
    let halfDayDate = null;
    let halfDaySide = null;
    if (halfDay) {
      // The half-day is whichever half is "half_day"
      if (halfDay.first_half === "half_day") {
        halfDayDate = start; // first half of start date
        halfDaySide = "first";
      } else if (halfDay.second_half === "half_day") {
        halfDayDate = end; // second half of end date
        halfDaySide = "second";
      }
    }

    const months = [];

    // Always render start month
    months.push(
      generateMonth(
        startYear,
        startMonth,
        start,
        end,
        halfDayDate,
        halfDaySide,
      ),
    );

    // If range spans different month/year → render end month
    if (startMonth !== endMonth || startYear !== endYear) {
      months.push(
        generateMonth(endYear, endMonth, start, end, halfDayDate, halfDaySide),
      );
    }

    return (
      <div
        className={`micro-calendar ${showAbove ? "micro-calendar-top" : ""}`}
        ref={tooltipRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="micro-months-wrapper">{months}</div>
      </div>
    );
  };

  const getLeaveString = (type) => {
    switch (type) {
      case "Sick Leave":
      case "SL":
        return "Sick";
      case "Casual Leave":
      case "CL":
        return "Casual";
      case "Privilege Leave":
      case "PL":
        return "Privilege";
      case "Maternity":
      case "Maternity Leave":
        return "Maternity";
      case "Menstrual":
      case "Menstrual Leave":
        return "Menstrual";
      default:
        return "Casual";
    }
  };

  return (
    <div className="leave-history">
      <div className="leave-hs-head">leave history</div>

      <div
        className="leave-hs-list"
        style={{
          paddingBottom: paddingBottomStr,
          transition: "padding-bottom 0.3s ease",
        }}
      >
        {leaveDetailResponseStatus === "PENDING" && <PendingCardSkeletons />}
        {leaveDetailResponseStatus !== "PENDING" && (
          <>
            {!leaveDetailResponse?.body?.length && (
              <div className="flex flex-col items-center justify-center h-full w-full">
                <img className="rp-empty-ico" src={NothingIcon} alt="" />
                <div className="rp-empty-text">No data available</div>
              </div>
            )}
            {leaveDetailResponse?.body?.map((item, index) => {
              const showAbove =
                index > 2 && index >= leaveDetailResponse.body.length - 4;

              return (
                <div className="leave-hs-item" key={index}>
                  <div className="leave-hs-item-left">
                    <div
                      className="leave-td-wrap"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveTooltip(activeTooltip === index ? null : index);
                      }}
                      onMouseEnter={() => {
                        setActiveTooltip(
                          activeTooltip === index ? null : index,
                        );
                      }}
                      onMouseLeave={() => {
                        setActiveTooltip(null);
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="leave-td-text">
                        {item.applied_days}{" "}
                        {item.applied_days === 1 ? "Day" : "Days"}
                        {item.half_day && <span className="info-dot">i</span>}
                      </div>

                      {activeTooltip === index &&
                        renderMiniCalendar(
                          item.start_date,
                          item.end_date,
                          showAbove,
                          item.half_day,
                        )}
                    </div>

                    <div className="leave-hs-range">
                      <div className="leave-hs-start-wrap">
                        <div className="leave-hs-start-text">
                          {moment(item.start_date).format("DD MMM 'YY")}
                        </div>
                      </div>

                      <img
                        src={TwoArrowIcon}
                        alt="to"
                        className="leave-hs-range-arrow"
                      />

                      <div className="leave-hs-end-wrap">
                        <div className="leave-hs-end-text">
                          {moment(item.end_date).format("DD MMM 'YY")}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="leave-hs-main">
                    <div className="leave-hs-middle">
                      <div className="leave-hs-type">
                        <img
                          className="leave-hs-type-img"
                          src={
                            item.leave_type === "SL"
                              ? SickIcon
                              : item.leave_type === "CL"
                                ? CasualIcon
                                : item.leave_type === "PL"
                                  ? PrivilageIcon
                                  : item.leave_type === "Maternity"
                                    ? MaternityIcon
                                    : MenstrualIcon
                          }
                          alt=""
                        />
                        <div className="leave-hs-type-text">
                          {getLeaveString(item.leave_type)}
                        </div>
                      </div>

                      <div className="leave-hs-reason-text">{item.leave_remarks}</div>

                      {item.s3_url && (
                        <div
                          className="leave-hs-attachment"
                          style={{ cursor: "pointer" }}
                          onClick={() => window.open(item.s3_url, "_blank")}
                        >
                          <img
                            className="leave-hs-attachment-ico"
                            src={DocsIcon}
                            alt=""
                          />
                          <div className="leave-hs-attachment-text">1</div>
                        </div>
                      )}
                    </div>

                    <div className="leave-hs-right">
                      {item.revoke_comment && (
                        <div
                          className="leave-hs-remarks-pill"
                          onClick={() =>
                            setModalConfig({
                              isOpen: true,
                              message: item.revoke_comment,
                            })
                          }
                        >
                          <img
                            src={CommentIcon}
                            alt="remarks"
                            className="leave-hs-remarks-icon"
                          />
                          <span className="leave-hs-remarks-count">1</span>
                        </div>
                      )}
                      {item.approver_remarks && (
                        <div
                          className="leave-hs-remarks-pill"
                          onClick={() =>
                            setModalConfig({
                              isOpen: true,
                              message: item.approver_remarks,
                            })
                          }
                        >
                          <img
                            src={CommentIcon}
                            alt="remarks"
                            className="leave-hs-remarks-icon"
                          />
                          <span className="leave-hs-remarks-count">1</span>
                        </div>
                      )}
                      {item?.leave_approval_status?.toLowerCase() ===
                      "pending" ? (
                        <div className="leave-hs-alter">
                          <div
                            className="leave-hs-edit-wrap"
                            onClick={() => {
                              onEditLeave(item);
                            }}
                          >
                            <span
                              style={{ fontSize: "16px", color: "black" }}
                              class="material-symbols-rounded"
                            >
                              edit
                            </span>
                          </div>
                          <div
                            className="leave-hs-cancel-wrap"
                            onClick={() => {
                              onCancelLeave(item);
                            }}
                          >
                            <span
                              style={{ fontSize: "16px", color: "black" }}
                              class="material-symbols-rounded"
                            >
                              close
                            </span>
                            <div className="leave-hs-cancel-text">Cancel</div>
                          </div>
                        </div>
                      ) : (
                        <div
                          className={`leave-hs-status-wrap leave-hs-status-${item.leave_approval_status?.toLowerCase()}`}
                        >
                          <img
                            src={
                              item.leave_approval_status?.toLowerCase() ===
                              "approved"
                                ? ApproveStatusIcon
                                : item.leave_approval_status?.toLowerCase() ===
                                      "rejected" ||
                                    item.leave_approval_status?.toLowerCase() ===
                                      "revoked"
                                  ? RejectStatusIcon
                                  : item.leave_approval_status?.toLowerCase() ===
                                      "cancel"
                                    ? CancelStatusIcon
                                    : PendingStatusIcon
                            }
                            alt=""
                            className="leave-hs-status-ico"
                          />
                          <div className="leave-hs-status-text">
                            {item.leave_approval_status}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
      <MessageModal
        isOpen={modalConfig.isOpen}
        onClose={() => setModalConfig({ ...modalConfig, isOpen: false })}
        message={modalConfig.message}
        empName={empName}
      />
    </div>
  );
};

export default LeaveHistory;
